import { useEffect, useState } from 'react';
import { Aviso, Boton, Cargando, Tarjeta } from './ui';
import { guardarApagadas, leerApagadas, SECCIONES, type TipoPlaca } from './seccionesPlaca';

/**
 * Una placa (imagen para el grupo): se arma, se muestra, y recién ahí se manda.
 * La usan la de la noche y la del mes.
 *
 * En dos toques y no en uno a propósito: el menú de compartir del celular
 * tiene que abrirse desde un toque, y armar la imagen tarda lo suficiente
 * como para que Safari ya no lo considere "el toque" y lo bloquee.
 *
 * Entre los dos toques se pueden prender y apagar secciones: los datos se
 * piden una sola vez y cada cambio solo vuelve a dibujar.
 */
export function PlacaImagen<T>({
  tipo,
  cargar,
  dibujar,
  disponibles,
  archivo,
  titulo,
  descripcion,
  onCerrar,
}: {
  tipo: TipoPlaca;
  /** Pide los datos. Se llama una vez por cada `archivo`. */
  cargar: () => Promise<T>;
  dibujar: (datos: T, apagadas: ReadonlySet<string>) => Promise<Blob>;
  /** Las secciones que estos datos tienen para mostrar. */
  disponibles: (datos: T) => string[];
  archivo: string;
  titulo: string;
  descripcion: string;
  onCerrar: () => void;
}) {
  const [datos, setDatos] = useState<T | null>(null);
  const [apagadas, setApagadas] = useState(() => leerApagadas(tipo));
  const [imagen, setImagen] = useState<{ blob: Blob; url: string } | null>(null);
  const [dibujando, setDibujando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    cargar()
      .then((d) => vivo && setDatos(d))
      .catch((e: unknown) => vivo && setError(e instanceof Error ? e.message : 'No se pudo armar.'));
    return () => {
      vivo = false;
    };
    // `cargar` cambia en cada render; lo que define la placa es el archivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivo]);

  useEffect(() => {
    if (datos === null) return;
    let vivo = true;
    let url: string | null = null;
    setDibujando(true);
    dibujar(datos, apagadas)
      .then((blob) => {
        if (!vivo) return;
        url = URL.createObjectURL(blob);
        setImagen({ blob, url });
      })
      .catch((e: unknown) => vivo && setError(e instanceof Error ? e.message : 'No se pudo armar.'))
      .finally(() => vivo && setDibujando(false));
    return () => {
      vivo = false;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datos, apagadas]);

  function alternar(id: string) {
    const nuevas = new Set(apagadas);
    if (nuevas.has(id)) nuevas.delete(id);
    else nuevas.add(id);
    guardarApagadas(tipo, nuevas);
    setApagadas(nuevas);
  }

  async function mandar() {
    if (!imagen) return;
    const png = new File([imagen.blob], archivo, { type: 'image/png' });
    if (navigator.canShare?.({ files: [png] })) {
      try {
        await navigator.share({ files: [png], title: titulo });
        return;
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
      }
    }
    // En la compu (o un navegador sin compartir archivos): se descarga.
    const a = document.createElement('a');
    a.href = imagen.url;
    a.download = png.name;
    a.click();
    setAviso('Se descargó la imagen: mandala desde la galería o la carpeta de descargas.');
  }

  const hay = datos === null ? [] : disponibles(datos);
  const secciones = SECCIONES[tipo].filter((s) => hay.includes(s.id));

  return (
    <Tarjeta className="flex flex-col gap-3 border-(--color-acento)">
      {error ? (
        <Aviso tono="error">{error}</Aviso>
      ) : !imagen ? (
        <Cargando que="Armando la placa" />
      ) : (
        <img
          src={imagen.url}
          alt={descripcion}
          className={`w-full rounded-xl border border-(--color-borde) transition-opacity
            ${dibujando ? 'opacity-60' : ''}`}
        />
      )}
      {imagen && secciones.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-(--color-apagado)">Qué sale en la imagen:</p>
          <div className="flex flex-wrap gap-1.5">
            {secciones.map((s) => {
              const prendida = !apagadas.has(s.id);
              return (
                <button
                  key={s.id}
                  onClick={() => alternar(s.id)}
                  aria-pressed={prendida}
                  className={`min-h-9 rounded-full border px-3 text-sm transition active:scale-[0.97]
                    ${
                      prendida
                        ? 'border-(--color-acento) bg-(--color-acento) font-semibold text-(--color-sobre-acento)'
                        : 'border-(--color-borde) text-(--color-apagado) line-through'
                    }`}
                >
                  {s.nombre}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {aviso && <Aviso>{aviso}</Aviso>}
      <div className="flex gap-2">
        {imagen && (
          <Boton ancho onClick={mandar} deshabilitado={dibujando}>
            Mandar al grupo
          </Boton>
        )}
        <Boton tipo="secundario" onClick={onCerrar}>
          Cerrar
        </Boton>
      </div>
    </Tarjeta>
  );
}
